@file:OptIn(ExperimentalWasmJsInterop::class)

package fr.arcplex.sooner

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.DelicateCoroutinesApi
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.*

@JsFun("""
(id, url, drmUrl, payloadJson, provisionUrl, vudrmToken) => {
    const prev = document.getElementById(id);
    if (prev) { prev.remove(); }
    const prevBack = document.getElementById(id + '-back');
    if (prevBack) { prevBack.remove(); }
    const video = document.createElement('video');
    video.id = id;
    video.style.position = 'fixed';
    video.style.top = '0';
    video.style.left = '0';
    video.style.width = '100vw';
    video.style.height = '100vh';
    video.style.zIndex = '9999';
    video.style.backgroundColor = '#000';
    video.controls = true;
    video.autoplay = false;
    document.body.appendChild(video);

    // DOM back button above the fullscreen video overlay (the video covers
    // the Compose canvas, so the Compose back bar is not clickable while
    // playing). Clicking it removes the video; the Kotlin polling loop below
    // observes the removal and fires onStop -> PageManager.goBack().
    const backBtn = document.createElement('button');
    backBtn.id = id + '-back';
    backBtn.textContent = '‹ Back';
    backBtn.style.position = 'fixed';
    backBtn.style.top = '12px';
    backBtn.style.left = '12px';
    backBtn.style.zIndex = '10000';
    backBtn.style.padding = '8px 16px';
    backBtn.style.fontSize = '16px';
    backBtn.style.cursor = 'pointer';
    backBtn.onclick = () => {
        try { video.pause(); } catch (e) {}
        video.remove();
        backBtn.remove();
    };
    document.body.appendChild(backBtn);

    if (window.shaka) {
        const player = new shaka.Player();
        player.attach(video);

        // Production parity (shaka_player.html.twig): Widevine licenses go DIRECT to
        // the Vualto proxy carrying X-VUDRM-TOKEN (product-bound token from
        // getInfosViewVideoWebV2 / FilmController). The getLicence relay mints a
        // generic product-less token and returns bl_ok:0 for these assets, so it
        // is only kept as a token-less fallback.
        const certUrl = provisionUrl || 'https://widevine-license.vudrm.tech/proxy';

        console.log('[sooner-drm] setup: url=' + url + ' drmUrl=' + drmUrl + ' certUrl=' + certUrl + ' hasToken=' + (!!vudrmToken));
        console.log('[sooner-drm] env: isSecureContext=' + window.isSecureContext + ' ua=' + navigator.userAgent);
        console.log('[sooner-drm] eme present: ' + (!!navigator.requestMediaKeySystemAccess));
        if (navigator.requestMediaKeySystemAccess) {
            ['com.widevine.alpha', 'com.microsoft.playready', 'com.apple.fps'].forEach((ks) => {
                navigator.requestMediaKeySystemAccess(ks, [{
                    initDataTypes: ['cenc'],
                    audioCapabilities: [{ contentType: 'audio/mp4;codecs="mp4a.40.2"' }],
                    videoCapabilities: [{ contentType: 'video/mp4;codecs="avc1.42E01E"' }],
                }]).then(
                    () => console.log('[sooner-drm] key-system AVAILABLE: ' + ks),
                    (e) => console.log('[sooner-drm] key-system UNAVAILABLE: ' + ks + ' :: ' + (e && e.message)));
            });
        }

        const licenseRequestFilter = (type, request, context) => {
            if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;
            if (vudrmToken) {
                const size = request.body ? request.body.byteLength : 0;
                console.log('[sooner-drm] license request (' + size + 'B) -> ' + certUrl + ' (direct vudrm)');
                request.uris[0] = certUrl;
                request.headers['X-VUDRM-TOKEN'] = vudrmToken;
                delete request.headers['Content-Type'];
                return;
            }
            console.log('[sooner-drm] license request (' + (request.body ? request.body.byteLength : 0) + 'B) -> ' + (request.uris ? request.uris[0] : request.uri) + ' (getLicence fallback, no token)');
            let payloadObj = {};
            try {
                if (payloadJson && payloadJson !== "") {
                    payloadObj = JSON.parse(payloadJson);
                }
            } catch (e) {}
            const drmRequest = shaka.util.Uint8ArrayUtils.toBase64(new Uint8Array(request.body));
            const params = new URLSearchParams();
            params.append("id_programme", payloadObj["id_programme"] || "");
            params.append("id_client", payloadObj["id_client"] || "");
            params.append("type", payloadObj["type"] || "");
            params.append("user_token", payloadObj["user_token"] || "");
            params.append("type_drm", "widevine");
            params.append("drmRequest", drmRequest);
            request.headers['Content-Type'] = 'application/x-www-form-urlencoded';
            request.body = new TextEncoder().encode(params.toString());
        };

        const licenseResponseFilter = (type, response, context) => {
            if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;
            if (!response.data) return;
            let wrappedString;
            try {
                wrappedString = new TextDecoder().decode(response.data);
            } catch (e) { return; }
            let json;
            try {
                json = JSON.parse(wrappedString);
            } catch (e) {
                console.log('[sooner-drm] response passthrough (not license JSON, likely service cert, ' + response.data.byteLength + 'B)');
                return;
            }
            const licence = json && json.str_licence;
            if (typeof licence === 'string' && licence.length > 0) {
                console.log('[sooner-drm] license response: unwrapping str_licence');
                try {
                    response.data = shaka.util.Uint8ArrayUtils.fromBase64(licence);
                } catch (e) {}
            } else {
                let keys = '';
                try { keys = Object.keys(json || {}).join(','); } catch (e) {}
                const snippet = wrappedString.length > 500 ? wrappedString.substring(0, 500) + '…' : wrappedString;
                console.log('[sooner-drm] response JSON without str_licence (keys=[' + keys + '] size=' + response.data.byteLength + 'B): ' + snippet);
            }
        };

        player.getNetworkingEngine().registerRequestFilter(licenseRequestFilter);
        player.getNetworkingEngine().registerResponseFilter(licenseResponseFilter);

        player.addEventListener('error', (event) => {
            const d = event.detail || {};
            console.error('[sooner-drm] Shaka player error: code=' + d.code + ' category=' + d.category + ' message=' + (d.message || d.stack || d));
        });

        player.configure({
            drm: {
                servers: {
                    'com.widevine.alpha': drmUrl || '',
                    'com.microsoft.playready': drmUrl || '',
                    'com.apple.fps': drmUrl || ''
                },
            }
        });

        player.load(url).then(() => {
            console.log('[sooner-drm] Video loaded successfully');
            const p = video.play();
            if (p && p.catch) {
                return p.catch((playErr) => {
                    console.log('[sooner-drm] autoplay blocked (non-fatal, use controls): name=' + (playErr && playErr.name) + ' message=' + (playErr && playErr.message));
                });
            }
        }).then(() => {
            console.log('[sooner-drm] Video playback started (or awaiting user play)');
        }).catch((error) => {
            console.error('[sooner-drm] Video load error: code=' + (error && error.code) + ' name=' + (error && error.name) + ' message=' + (error && (error.message || error.stack || error)));
        });
    }
}
""")
external fun setupShakaVideo(id: String, url: String, drmUrl: String?, payloadJson: String, provisionUrl: String?, vudrmToken: String?)

@JsFun("(id) => { const el = document.getElementById(id); if (el) { el.remove(); } const b = document.getElementById(id + '-back'); if (b) { b.remove(); } }")
external fun removeShakaVideo(id: String)

@JsFun("(id) => { var el = document.getElementById(id); return el ? (el.paused ? 'paused' : 'playing') : 'none'; }")
external fun getVideoPauseState(id: String): String

@JsFun("(id) => { var el = document.getElementById(id); var p = el ? el.player : null; return p ? (p.currentTime * 1000) + ',' + (p.duration * 1000) : '0,0'; }")
external fun getVideoPosition(id: String): String

@Composable
actual fun VideoPlayer(
    url: String,
    modifier: Modifier,
    drmLicenseUrl: String?,
    licensePayload: Map<String, String>?,
    drmProvisionUrl: String?,
    vudrmToken: String?,
    onPlay: (() -> Unit)?,
    onPause: (() -> Unit)?,
    onStop: (() -> Unit)?,
    onSeek: ((position: Long) -> Unit)?,
    onTick: ((position: Long, duration: Long) -> Unit)?,
    videoInfo: VideoInfo?
) {
    val videoId = remember(url) { "web-video-$url" }

    val payloadJson = remember(licensePayload) {
        if (licensePayload == null) ""
        else Json.encodeToString(licensePayload)
    }

    val provUrl = drmProvisionUrl ?: "https://widevine-license.vudrm.tech/proxy"

    var isPlaying by remember { mutableStateOf(false) }
    var lastPosition by remember { mutableStateOf<Long?>(null) }

    DisposableEffect(url, drmLicenseUrl, payloadJson, provUrl, vudrmToken) {
        setupShakaVideo(videoId, url, drmLicenseUrl, payloadJson, provUrl, vudrmToken)
        onDispose {
            removeShakaVideo(videoId)
        }
    }

    DisposableEffect(url, videoInfo, onPlay, onPause, onStop, onSeek, onTick) {
        GlobalScope.launch {
            var wasPlaying = false
            var wasReady = false
            while (true) {
                kotlinx.coroutines.delay(500)
                val pauseState = getVideoPauseState(videoId)
                val wasPlayingNow = pauseState != "paused"
                val isVideoReady = pauseState != "none"

                if (isVideoReady) {
                    wasReady = true
                    if (wasPlaying && !wasPlayingNow) {
                        onPause?.invoke()
                        if (pauseState == "idle" || pauseState == "ended") {
                            onStop?.invoke()
                        }
                    } else if (!wasPlaying && wasPlayingNow) {
                        onPlay?.invoke()
                    }

                    val posStr = getVideoPosition(videoId)
                    val parts = posStr.split(",")
                    val currentPos = parts.getOrNull(0)?.toLongOrNull() ?: 0L
                    val duration = parts.getOrNull(1)?.toLongOrNull() ?: 0L

                    if (currentPos > 0) {
                        if (lastPosition != null && currentPos != lastPosition!!) {
                            onSeek?.invoke(currentPos)
                        }
                        if (wasPlayingNow) {
                            onTick?.invoke(currentPos, duration)
                        }
                        lastPosition = currentPos
                    }

                    wasPlaying = wasPlayingNow
                } else if (wasReady) {
                    // Video element removed (DOM back button) -> closed, not paused.
                    wasReady = false
                    wasPlaying = false
                    onStop?.invoke()
                }

                isPlaying = wasPlayingNow
            }
        }
        onDispose { }
    }

    Box(modifier = modifier) {
        Spacer(modifier = modifier)

        if (videoInfo != null && !isPlaying) {
            VideoPlayerOverlay(videoInfo)
        }
    }
}
